// Brazilian CPF / CNPJ digit-check algorithms.
// CPF: 11 digits, check digits computed from the first 9.
// CNPJ: delegated to the Effect CNPJ, the only home of its check-digit rule.

import { hasCnpjCheckDigits } from '../../../effect/schemas/br/cnpj.ts';

function onlyDigits(s: string): string {
  return s.replaceAll(/\D/gu, '');
}

function isRepeated(s: string): boolean {
  return new Set(s).size === 1;
}

const CPF_LENGTH = 11;
/** Digits 0..8 feed the first CPF check digit. */
const CPF_FIRST_CHECK_SOURCE_LENGTH = 9;
/** Digits 0..9 — the first nine plus the first check digit — feed the second. */
const CPF_SECOND_CHECK_SOURCE_LENGTH = 10;
/** Position of the first check digit inside the 11-digit CPF. */
const CPF_FIRST_CHECK_POSITION = 9;
/** Position of the second check digit inside the 11-digit CPF. */
const CPF_SECOND_CHECK_POSITION = 10;
const CPF_CHECK_SCALE = 10;
const CPF_CHECK_MODULUS = 11;
/** The single remainder the modulus can yield that is not itself a digit. */
const CPF_CHECK_REMAINDER_OVERFLOW = 10;

function cpfCheckDigit(digits: string, length: number): number {
  let sum = 0;
  for (let i = 0; i < length; i += 1) {
    sum += Number.parseInt(digits.charAt(i), 10) * (length + 1 - i);
  }
  const rest = (sum * CPF_CHECK_SCALE) % CPF_CHECK_MODULUS;
  return rest === CPF_CHECK_REMAINDER_OVERFLOW ? 0 : rest;
}

export function validateCpfDigits(input: string): boolean {
  const digits = onlyDigits(input);
  if (digits.length !== CPF_LENGTH) return false;
  if (isRepeated(digits)) return false;
  const firstCheck = cpfCheckDigit(digits, CPF_FIRST_CHECK_SOURCE_LENGTH);
  const secondCheck = cpfCheckDigit(digits, CPF_SECOND_CHECK_SOURCE_LENGTH);
  return (
    firstCheck === Number.parseInt(digits.charAt(CPF_FIRST_CHECK_POSITION), 10) &&
    secondCheck === Number.parseInt(digits.charAt(CPF_SECOND_CHECK_POSITION), 10)
  );
}

/** A numeric CNPJ, punctuation ignored; the check digits come from `@finnest/core/effect`. */
export function validateCnpjDigits(input: string): boolean {
  return hasCnpjCheckDigits(onlyDigits(input));
}
